import {Component, OnChanges, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, NavigationEnd, NavigationStart, Router} from '@angular/router';
import {TealiumhelperService} from './../shared/analytics/tealiumhelper/tealiumhelper.service';
import {TealiumdataService} from './../shared/analytics/tealiumdata/tealiumdata.service';
import {tealiumConstants} from './../shared/analytics/tealium.constants';
import {rootUrl} from './../shared/config/config/config.constants';
import {SpinnerService} from './../shared/services/spinner/spinner.service';
import {filter, map} from 'rxjs/internal/operators';
import {StorageService} from './../shared/services/storage/storage.service';
import {AlertMessage} from './../shared/contracts/alert-message';
import {GlobalAlertService} from './../shared/services/global-alert/global-alert.service';
import {takeUntil} from 'rxjs/operators';
import {Subject, Subscription} from 'rxjs';
import {Order} from './../shared/contracts/order';
import {UtilityCookieService as utilityCookie} from '../shared/utils/cookie/utilitycookie.service';
import {UiConfigService} from '../shared/config/ui-config/ui-config.service';
import {Title} from '@angular/platform-browser';
import {MemberEventsService} from '../shared/services/member-events/member-events.service';
import {XidService} from '../shared/services/xid/xid.service';
import {ProfileMaintenanceService} from './../shared/services/profile-maintenance/profile-maintenance.service';
import {OrderViewData} from './../shared/contracts/order-view-data';
import {LoginService} from './../shared/services/login/login.service';
import {OrderDetailsService} from './../shared/services/order-details/order-details.service';
import {Location} from '@angular/common';
import {queryParamObjectToString} from './../shared/utils/util';
import {IUserData} from './../shared/contracts/userData';

declare function require(path: string);

@Component({
  selector: 'app-cvs-mdp-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  private routesSub: any;
  private destroy: Subject<boolean> = new Subject<boolean>();
  public rxTied;
  public user: any = this._activatedRoute.snapshot.data['user'];
  public orderHistory: any[];
  public nextOrders: any;
  public previousOrders: any;
  public url: string = this._router.url.split('?')[0];
  public hide: boolean;
  public activeMember: boolean;
  public errorView: boolean;
  private userType: string = '';
  public noOrders = false;
  public improve_health = require('./../../assets/images/improve-health.png');
  public isSyncPlanApproved = false;
  public isSyncPlanSwitch: boolean;
  public syncPlan: any;
  public mdpEnrolled: boolean;
  public isAutoFillEnabledOrDisabled: boolean;

  // ordered as per UX spec
  optList = [
    {
      text: 'Manage medications for those you care for.',
      iconName: 'tablet'
    },
    {
      text: 'Use any mobile or desktop device to access your account.',
      iconName: 'phone'
    },
    {
      text: 'Check your SimpleDose order status easily.',
      iconName: 'magnifier'
    }
  ];
  /**
   * Orders list
   */
  public activeOrders: any;
  /**
   * Alert message placeholder
   */
  alert: AlertMessage = {};
  /**
   * Template configuration
   * see app/shared/config/config/config.constants.ts
   */
  viewConfig: any = {};

  constructor(private _router: Router,
              private _order: OrderDetailsService,
              private _spinner: SpinnerService,
              private _storage: StorageService,
              private _tagHelperService: TealiumhelperService,
              private _tealiumDataService: TealiumdataService,
              private _activatedRoute: ActivatedRoute,
              private _globalAlert: GlobalAlertService,
              private _uiConfig: UiConfigService,
              private _loginService: LoginService,
              private _memberEvents: MemberEventsService,
              private _titleService: Title,
              private _xid: XidService,
              private profileService: ProfileMaintenanceService,
              private _location: Location) {
  }

  /**
   * Angular <LifeCycle> method
   */
  ngOnInit() {
    this.url = this._router.url.split('?')[0];
    this.viewConfig = this._uiConfig.setup(this.url);
    const appTitle = this._titleService.getTitle();
    // check member type caregivee
    this.activeMember = this._loginService.activeMember;
    this.routesSub = this._router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd || event instanceof NavigationStart)
    ).forEach((e) => {
      if (e instanceof NavigationEnd) {
        this.url = this._router.url.split('?')[0];
        this.viewConfig = this._uiConfig.setup(this.url);
        this.hide = false;
        // to change page title depending on route
        const child = this._activatedRoute.firstChild;
        if (child.snapshot.data['title']) {
          this._titleService.setTitle(child.snapshot.data['title']);
        } else {
          this._titleService.setTitle(appTitle);
        }
      } else if (e instanceof NavigationStart) {
        this.hide = true;
      }
    });
    this._globalAlert.msg.pipe(takeUntil(this.destroy)).subscribe(alert => {
      // for accessibility
      setTimeout(() => this.alert = alert);
    });
    this.user = this._activatedRoute.snapshot.data['user'];
    this.userType = this.checkUserType(this.user);
    const orderViewData: OrderViewData = this.getOrders();
    /**
     * Trigger member events to capture traffic coming from email
     */
    this.triggerMemberEvents();
    /**
     * Check for xid and trigger member events
     */
    const xid = this._activatedRoute.snapshot.queryParams.xid;
    if (xid) {
      this._xid.check(xid, this.user).toPromise().then((r) => {
        if (r) {
          this.triggerMemberEvents({xid, opportunityID: r.opportunityID, campaignID: r.campaignID});
        }
      });
    }
    /**
     * Check if selected member has email address on file - Only if is mdp enrolled
     */
    if (orderViewData) {
      this.profileService.checkEmail(this.user, orderViewData.memberInfo);
    }
    /**
     * Check if sync plan cookie is on, redirect any urls to /sync-plan if enrolled in program and in sync plan
     */
    this.isSyncPlanSwitch = (utilityCookie.getCookie('sync_plan_dashboard') === 'on');
    if (this.isSyncPlanApproved && this.isSyncPlanSwitch) {
      const params = queryParamObjectToString(this._activatedRoute.snapshot.queryParams);
      // set default url to sync-plan when active  user is in sync plan
      // TODO: merge sync-plan with orders component if possible
      this._uiConfig.defaultTabUrl = '/sync-plan';
      this._location.replaceState('sync-plan', params);
      return this._router.navigate(['/sync-plan'], {preserveQueryParams: true, skipLocationChange: true});
    }
    /**
     * Redirect any urls to /orders if enrolled in program and no tab is identified
     */
    const tab = (this._router.url).match(/[A-Za-z]+/);
    if ((!tab || typeof tab !== 'string') && this.activeOrders && this.activeOrders.isMdpEnrolled) {
      const params = queryParamObjectToString(this._activatedRoute.snapshot.queryParams);
      // set default url to orders when active user is not sync plan
      // TODO: merge sync-plan with orders component if possible
      this._uiConfig.defaultTabUrl = '/orders';
      this._location.replaceState('orders', params);
      return this._router.navigate(['/orders'], {preserveQueryParams: true, skipLocationChange: true});
    }

    /**
     * Send analytics
     */
    if (this.noOrders) {
      this.sendTag('interim');
    } else if (this.activeOrders && !this.activeOrders.isMdpEnrolled) {
      this.sendTag('program-enroll', {'patientcaregiver_flag': this.userType});
    } else if (this.errorView) {
      this.sendTag('error', {
        field_errors: JSON.stringify(this.activeOrders)
      });
    }
  }

  /**
   * userType added to view tag
   * Todo: move checkUserType to common reusable file
   */
  private checkUserType(user: IUserData): string {
    const userType = user && user.caregivee ? 'caregivee' : user && user.careGiver ? 'caregiver' : 'patient';
    return userType;
  }

  /**
   * Get orders list and set view variables
   */
  getOrders(): OrderViewData {
    const orders = this.activeOrders = this._activatedRoute.snapshot.data['orders'];
    if (orders && !orders.hasError) {
      this.isSyncPlanApproved = orders.isSyncPlanApproved;
      if (this.isSyncPlanApproved) {
        this.syncPlan = orders.allOrders[0];
      }
      this.noOrders = orders.noOrders;
      this.nextOrders = orders.nextOrders;
      // console.log('nextOrders', this.nextOrders);
      this.orderHistory = orders.orderHistory;
      // console.log('orderHistory', this.orderHistory);
    } else {
      this.errorView = true;
    }
    return orders;
  }

  /**
   * Angular <LifeCycle> method
   */
  ngOnDestroy() {
    try {
      this.routesSub.unsubscribe();
    } catch (e) {
    }
    this.destroy.next(true);
    // unsubscribe from the subject itself:
    this.destroy.unsubscribe();
  }

  /**
   * Redirect user to rx tie flow
   */
  public redirectToRxTie() {
    this.sendTag('access-mdp');
    this._spinner.show();
    this._storage.sessionStorage.removeItem('memberDetails');
    setTimeout(() => {
      // https://www-qa2.cvs.com/pharmacy/rx-manage/#/rxtie
      // https://www-qa2.cvs.com/pharmacy/v1/#/cg/cgrxmanage
      this._storage.sessionStorage.setItem('rxmInfo', JSON.stringify({
        'exitURL': rootUrl + '/content/multidose',
        'exitURLTitle': 'Return to SimpleDose home',
        'successURL': rootUrl + '/pharmacy/mdp-dashboard/orders',
        'successURLTitle': 'Go to SimpleDose'
      }));
      window.location.href = rootUrl + '/pharmacy/rx-manage/rxtie';
    }, 500);
  }

  /**
   * Redirect to select user
   */
  public manageAccount() {
    this._order.flush();
    this._storage.sessionStorage.removeItem('activeMemberIndex');
    this.sendTag('switchLink');
    this._router.navigate(['select-user']);
  }

  /**
   * Capture queryParameters and send memberEvent if
   * opportunityId and CampaignId are present in URL
   */
  triggerMemberEvents(params?: any): void {
    const queryParam: any = params || this._activatedRoute.snapshot.queryParams;
    if (queryParam.opportunityID && queryParam.campaignID) {
      this._uiConfig.saveQueryParams(queryParam, 'merge');
      // if xid is present, then it is sms flow
      const evtName: string = 'MDP_DB_LOAD_CRM_EMAIL';
      this._memberEvents.trigger(evtName, [{
        opportunityID: queryParam.opportunityID,
        campaignID: queryParam.campaignID,
        dispositionCode: (!this.errorView ? '4002' : '3000')
      }]);
    }
  }

  /**
   * Send analytics data
   */
  sendTag(type, extras = {}) {
    let data = {};
    if (type === 'interim') {
      data = Object.assign(this._tealiumDataService.getDefault(null,
        `rx:mdp:dashboard:no orders - interim state`, 'rx:mdp'), extras);
      this._tagHelperService.view(data);
    } else if (type === 'access-mdp') {
      data = Object.assign(this._tealiumDataService.getDefault(null,
          'rx:mdp:access multi-dose',
          tealiumConstants.pageCategory),
        tealiumConstants.accessMdp);
      this._tagHelperService.link(data);
    } else if (type === 'switchLink') {
      data = Object.assign(this._tealiumDataService.getDefault(null,
        'rx:mdp:dashboard', 'rx:mdp'), tealiumConstants.manageAnotherAccount, extras);
      this._tagHelperService.link(data);
    } else if (type === 'error') {
      data = Object.assign(
        this._tealiumDataService.getDefault(null, `rx:mdp:error`, 'rx:mdp')
        , extras);
      this._tagHelperService.view(data);
    } else if (type === 'program-enroll') {
      data = Object.assign(
        this._tealiumDataService.getDefault(null, `rx:mdp:dashboard:enroll in simpledose`, 'rx:mdp')
        , extras);
      this._tagHelperService.view(data);
    }
  }

}
